import api from './api';

/**
 * Team Data Structure:
 * {
 *   id: string,
 *   name: string,
 *   managerName: string,
 *   managerEmail: string,
 *   role: 'manager' | 'director' | 'avp',
 *   parentTeamId: string | null,  // ID of the parent team (director/AVP)
 *   activities: Array<productId>,  // IDs of assigned activities
 *   userId: string,
 *   createdAt: timestamp,
 *   updatedAt: timestamp
 * }
 */

export const getTeams = async () => {
  try {
    const response = await api.get('/teams');
    return response.data;
  } catch (error) {
    console.error('Error fetching teams:', error);
    throw error;
  }
};

export const getTeamById = async (teamId) => {
  try {
    const response = await api.get(`/teams/${teamId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching team:', error);
    throw error;
  }
};

export const createTeam = async (teamData) => {
  try {
    const response = await api.post('/teams', teamData);
    return response.data;
  } catch (error) {
    console.error('Error creating team:', error);
    throw error;
  }
};

export const updateTeam = async (teamId, updates) => {
  try {
    const response = await api.put(`/teams/${teamId}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating team:', error);
    throw error;
  }
};

export const deleteTeam = async (teamId) => {
  try {
    const response = await api.delete(`/teams/${teamId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting team:', error);
    throw error;
  }
};

/**
 * Get organizational hierarchy
 * Returns structure like:
 * {
 *   avp: { team, directors: [ { team, managers: [ { team, activities, fteTotal } ] } ] }
 * }
 */
export const getTeamHierarchy = async () => {
  try {
    const response = await api.get('/teams/hierarchy');
    return response.data;
  } catch (error) {
    console.error('Error fetching team hierarchy:', error);
    throw error;
  }
};

/**
 * Get rollup data for a specific team (director or AVP view)
 * Includes aggregated FTEs, activities, and justifications from subordinate teams
 */
export const getTeamRollup = async (teamId) => {
  try {
    const response = await api.get(`/teams/${teamId}/rollup`);
    return response.data;
  } catch (error) {
    console.error('Error fetching team rollup:', error);
    throw error;
  }
};

/**
 * Assign activities to a team
 */
export const assignActivitiesToTeam = async (teamId, activityIds) => {
  try {
    const response = await api.post(`/teams/${teamId}/activities`, { activityIds });
    return response.data;
  } catch (error) {
    console.error('Error assigning activities:', error);
    throw error;
  }
};

/**
 * Check team setup status
 */
export const checkSetupStatus = async () => {
  try {
    const response = await api.get('/teams/setup-status');
    return response.data;
  } catch (error) {
    console.error('Error checking setup status:', error);
    throw error;
  }
};

/**
 * Complete team setup
 */
export const completeTeamSetup = async (setupData) => {
  try {
    const response = await api.post('/teams/setup', setupData);
    return response.data;
  } catch (error) {
    console.error('Error completing setup:', error);
    throw error;
  }
};
